/** @jsx isolateComponent */
import React from 'react';

import { isolateComponent } from '@zapier/style-encapsulation';
import { Spacer } from '@zapier/design-system';

import List from '../../shared/List';
import RadioInput from '../..//shared/RadioInput';
import Surface from '../..//shared/Surface';
import { SizeType } from '../../../types';

type IconName = any;
type Item = { icon?: IconName; label: string; value: string };

type ErrorBlockProps = {
  /** Title */
  title: string;
  /** Custom error message to display as separate option, but only if `onClickCustom` is also set. */
  customError?: string;
  /** Radio inputs to list */
  items: Item[];
  /** Function to call when the user clicks on the custom error. */
  onClickCustom?: () => void;
  /** Function to call when the user clicks on an item. */
  onClickItem: (item: Item) => void;
  /** Size to display. */
  size?: SizeType;
};

const CustomError = ({
  message,
  onClick,
}: {
  message?: string;
  onClick?: () => void;
}) => {
  if (!message || !onClick) {
    return null;
  }
  return (
    <React.Fragment>
      <p>It looks like you were troubleshooting an error</p>
      <Spacer height={10} />
      <RadioInput icon="alertTriangleFill" label={message} onClick={onClick} />
      <Spacer height={20} />
      <p>Other issues</p>
      <Spacer height={10} />
    </React.Fragment>
  );
};

/**
 * Renders a block with a list of items using the `RadioInput` component.
 */
const ErrorBlock = ({
  title,
  customError,
  items: items,
  onClickCustom,
  onClickItem,
  size = 'large',
}: ErrorBlockProps) => {
  return (
    <Surface size={size} title={title}>
      <Spacer height={20} />
      <CustomError message={customError} onClick={onClickCustom} />
      <List
        gap={5}
        itemRenderer={(item: Item) => (
          <RadioInput label={item.label} onClick={() => onClickItem(item)} />
        )}
        items={items}
      />
    </Surface>
  );
};

export default ErrorBlock;
